import { Box, Loader, Spacer, Text } from '@jfs/ui-new'
import { useRoute } from '@react-navigation/native'
import { FC, useEffect, useState } from 'react'
import ActionsMenu from '../../comps/ActionMenu/ActionsMenu'
import Alert, { AlertTypes } from '../../comps/Alert'
import EmptyView from '../../comps/EmptyView'
import { ModBox } from '../../comps/ModifiedBox/ModifiedBox'
import { NewTable } from '../../comps/NewTable'
import { TimeOut, Toast, ToastPosition, ToastType } from '../../comps/Toast'
import { downloadReportsListTitleData } from '../../data/static/data'
import { transactionScreenStrings } from '../../data/static/strings'
import { useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import {
  convertToCaps,
  getCustomColumnWidth,
  getErrorMessage,
  getLoadingState,
  useWindowDimensions,
} from '../../util/util'
import { styles } from './styles'

interface DownloadsInterface {}

const Downloads: FC = ({}: DownloadsInterface) => {
  const path = useRoute().name
  const viewPortWidth = useWindowDimensions()
  const {
    fetchDownloadsListApiList,
    getDownloadReportData,
    reportType,
    pageNum,
    deleteReportFile,
    downloadReportFile,
    mid,
    setDownloadReportType,
  } = useStores((store) => ({
    fetchDownloadsListApiList: store.reports.fetchDownloadsListApiList,
    getDownloadReportData: store.reports.getDownloadReportData,
    reportType: store.settings.metaData.downloadReportType,
    pageNum: store.reports.pagination.number,
    deleteReportFile: store.reports.deleteReportFile,
    downloadReportFile: store.reports.downloadReportFile,
    mid: store.user.selectedMid,
    setDownloadReportType: store.settings.metaData.setDownloadReportType,
  }))
  const reportTypeFromRoute = convertToCaps(path?.split('/')?.[1] ?? (reportType || 'Transaction'))
  const [fieldSettingVisible, setFieldSettingVisible] = useState(false)
  const [filterVisible, setFilterVisible] = useState(false)
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const [showFileDeleted, setShowFileDeleted] = useState(false)
  const [itemId, setItemId] = useState('')
  const [fileName, setFileName] = useState('')

  const listData = getDownloadReportData()

  const [fetchDownloadsListApi, fetchDownloadsListHandler] = useLazyApiData(fetchDownloadsListApiList)
  const [deleteReportFileApi, deleteReportFileHandler] = useLazyApiData(deleteReportFile)
  const [downloadFileApi, downloadFileHandler] = useLazyApiData(downloadReportFile)

  const apis = [fetchDownloadsListHandler]
  const loadingState = getLoadingState(apis)
  const errorState = getErrorMessage(apis)

  useEffect(() => {
    reportTypeFromRoute && setDownloadReportType(reportTypeFromRoute)
  }, [reportTypeFromRoute, setDownloadReportType])

  useEffect(() => {
    if (reportType.length > 0 && mid.length > 0) {
      fetchDownloadsListApi(pageNum, reportType.toUpperCase(), mid)
    }
  }, [fetchDownloadsListApi, pageNum, reportType, mid])

  const getEmptyMessage = (): string => {
    return listData?.length === 0 ? transactionScreenStrings.noRecords : transactionScreenStrings.empty
  }

  const handleRetry = () => {
    if (reportType.length > 0) {
      fetchDownloadsListApi(pageNum, reportType.toUpperCase(), mid)
    }
  }
  const handleListItemPress = () => {}

  const onDownloadRefreshList = (isRefreshList: boolean) => {
    if (isRefreshList) {
      handleRetry()
    }
  }

  useEffect(() => {
    if (deleteReportFileHandler.state === 'fulfilled' && deleteReportFileHandler.data) {
      fetchDownloadsListApi(pageNum, reportType.toUpperCase(), mid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteReportFileHandler.state, fetchDownloadsListApi])

  const handleDeleteAlertBtnPress = () => {
    setShowDeleteAlert(false)
    deleteReportFileApi(itemId, reportType.toUpperCase())
    setShowFileDeleted(true)
  }

  const handleAlertDismiss = () => {
    setShowDeleteAlert(false)
  }

  const onItemDelete = (data: any) => {
    console.log('onItemDelete', { data })
    setFileName(data?.fileName)
    setItemId(data?.requestId)
    setShowDeleteAlert(true)
  }
  const onItemDownload = (data: any) => {
    console.log('onItemDownload', { data })
    setItemId(data?.requestId)
    downloadFileApi(data?.requestId, reportType.toUpperCase())
  }

  return (
    <Box {...styles.container}>
      <Alert
        type={AlertTypes.Confirm}
        title={'Are you sure you want to delete this file?'}
        subTitle={fileName}
        onPress={handleAlertDismiss}
        onAction={handleDeleteAlertBtnPress}
        visible={showDeleteAlert}
        confirmOkLabel={'Delete File'}
        confirmCancelLabel={"Don't Delete"}
      />
      <Toast
        message={'File deleted successfully'}
        hide={!showFileDeleted}
        position={ToastPosition.TOP}
        type={ToastType.POSITIVE}
        timeOut={TimeOut.FIVE_SECONDS}
        onDismiss={() => setShowFileDeleted(false)}
      />
      <Text fontFamily={'jiotype-medium'} title={'Downloads'} fontSize={20} />
      <ActionsMenu
        path={Routes.DOWNLOADS} //value Should be reports as its used to map the pagination in ActionsMenu
        fieldSettingVisible={fieldSettingVisible}
        setFieldSettingVisible={setFieldSettingVisible}
        filterVisible={filterVisible}
        setFilterVisible={setFilterVisible}
        hideFieldSettings={true}
        onDownloadRefreshList={onDownloadRefreshList}
      />
      <EmptyView
        onRetry={handleRetry}
        isLoading={loadingState}
        error={errorState}
        emptyMessage={getEmptyMessage()}
        loadingMessage={'Loading...'}
        hide={!loadingState && !errorState}
        loaderColor={'BLUE'}
      />
      <ModBox show={deleteReportFileHandler.state === 'pending' || downloadFileHandler.state === 'pending'}>
        <Loader color="PRIMARY" />
      </ModBox>
      <Spacer size={24} direction="vertical" />
      <Box {...styles.tableContainer(false)}>
        <NewTable
          data={listData}
          rowHeight={48}
          headerHeight={64}
          customWidthFunc={getCustomColumnWidth}
          tableWidth={viewPortWidth > 1200 ? viewPortWidth - 288 : viewPortWidth}
          headerRowKeys={downloadReportsListTitleData}
          onRowClick={handleListItemPress}
          hide={listData?.length === 0 || !(!loadingState && !errorState && !getEmptyMessage())}
          onDeleteReportFile={onItemDelete}
          onDownloadReportFile={onItemDownload}
          height={546}
          sortBy="requestTime"
        />
      </Box>
    </Box>
  )
}

export default Downloads
